/**
 * Provides an Apollo Link for error handling, mainly uses apollo-link-error with some custom logic.
 *
 * See https://github.com/benchling/aurelia/pull/5596 for more details, but at a high level:
 *
 * - This module is only used when our server code or downstream Apollo links throws exceptions
 * - Downstream Apollo link errors and backend non-resolver exceptions are "networkErrors". These are pretty
 *   bad.
 * - When a resolver throws an exception (authz or non-authz), we'll be given graphQLErrors.
 * - Traditionally, REST endpoints threw exceptions for authz, but we prefer resolvers to return
 *   Thing | InaccessibleInfo. For details on why, see MergeQueriesTest::test_non_null_root_raising_exc and
 *   the above PR.
 * - We do gracefully handle authz errors by showing a toast in case devs miss library code that still throws
 *   exceptions
 * - We similarly gracefully handle non-authz errors here by showing a toast
 */
import {ApolloError, ApolloLink, ServerError, ServerParseError} from '@apollo/client';
import {onError} from '@apollo/client/link/error';
import {captureException, captureMessage} from '@sentry/browser';
import {GraphQLError} from 'graphql';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import notifications from 'notifications';

import notifyGenericGraphQLError from './notifyGenericGraphQLError';

// Use if the app cannot gracefully recover (e.g. React component fails to render and does not have a retry UI component).
export const UNRECOVERABLE_ERROR_MESSAGE =
  'Sorry, an unexpected error occurred. Please refresh the page or reach out to support@benchling.com if the issue persists.';

// Use if the app cannot gracefully recover due to a network error.
export const UNRECOVERABLE_NETWORK_ERROR_MESSAGE =
  'Sorry, a network error occurred. Please check your internet connection and refresh the page or reach out to support@benchling.com if the issue persists.';

function isServerError(networkError: Error | ServerError | ServerParseError): networkError is ServerError {
  return networkError.name === 'ServerError';
}

function isServerParseError(
  networkError: Error | ServerError | ServerParseError
): networkError is ServerParseError {
  return networkError.name === 'ServerParseError';
}

function getDevMessage(networkError: Error | ServerError | ServerParseError): string | null {
  // In practice, result is actually an array of Record<>, not a Record<> as the type suggests.
  const casted = networkError as (Error | ServerError | ServerParseError) & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result?: Array<Record<any, any>> | null;
  };

  if (
    casted.result &&
    casted.result.length &&
    casted.result[0]?.errors &&
    casted.result[0].errors[0]?.message
  ) {
    return casted.result[0].errors[0].message;
  }
  return null;
}

/**
 * Returns true if the given `networkError` failed to connect to the backend server.
 */
export function isConnectionError(networkError: Error | ServerError | ServerParseError): boolean {
  if (isServerError(networkError) || isServerParseError(networkError)) {
    // Based on the apollo client implementation, this should not be possible, since the fetch Promise would
    // not resolve but instead throw an error. For robustness, we'll check for "Network request failed" case.
    // See:
    //  - https://github.com/apollographql/apollo-client/blob/v3.4.17/src/link/http/createHttpLink.ts#L153-L204
    //  - https://github.com/apollographql/apollo-client/blob/v3.4.17/src/link/http/parseAndCheckHttpResponse.ts
    return networkError.statusCode === 0;
  }
  if (networkError instanceof TypeError) {
    // Note that this is whatwg-fetch-implementation-specific, which is what we always use in the browser.
    // See: https://github.com/github/fetch/blob/v3.0.0/fetch.js#L455-L468
    return networkError.message === 'Network request failed';
  }
  return false;
}

function isServerCrash(networkError: Error | ServerError | ServerParseError): boolean {
  if (isServerError(networkError) || isServerParseError(networkError)) {
    return networkError.statusCode >= 500;
  }
  return false;
}

export function isHandledApolloError(event: PromiseRejectionEvent): boolean {
  // onNetworkError comprehensively handles any error type that may be surfaced, so if any consumer is
  // interested in knowing if the ApolloError has been handled, we unconditionally return true.
  return event.reason instanceof ApolloError;
}

function onNetworkError(networkError: Error | ServerError | ServerParseError): void {
  // networkError implies the query could not be executed successfully due to an issue somewhere downstream
  // (either in an inner link, or the server crashed/considered the doc invalid - note that resolvers
  // throwing errors will be returned as GraphQL errors, not network errors, so this is usually pretty bad).

  if (isConnectionError(networkError)) {
    // If the user has a connection error (e.g. loss of internet), there's no bug to fix, so we *could* skip
    // reporting entirely. However, we'd like to avoid having no visibility into this failure mode. While
    // a Datadog metric would be appropriate, our tooling (client/metrics/Metrics.ts) does not have a retry
    // mechanism. As a compromise, we continue to use Sentry, but deduplicate aggressively - where the issue
    // can be ignored.
    captureMessage(
      `Benchling's ApolloClient unable to reach server due to client-side network issue - ${networkError}`,
      {
        fingerprint: ['benchling', 'onNetworkError', 'connection-error'],
        level: 'info',
      }
    );
  } else if (isServerCrash(networkError)) {
    // Ignore status codes >= 500, as those are expected to have unexpected payloads, and the frontend
    // stacktrace is not actually useful - instead, we should further invest in our backend monitoring.
  } else {
    captureException(networkError);
  }

  // Regardless, let the user know something went wrong. In practice, the majority of our GraphQL usage is
  // via React components, which do not render contextual error messages / allow recovery via a "refresh" UI,
  // so we present a toast telling the user to refresh the page.
  const message = isConnectionError(networkError)
    ? UNRECOVERABLE_NETWORK_ERROR_MESSAGE
    : UNRECOVERABLE_ERROR_MESSAGE;
  notifyGenericGraphQLError(message);
  if (window.debug && getDevMessage(networkError) != null) {
    notifications.info(`[DEBUG] ${getDevMessage(networkError)}`);
  }
}

interface ErrorInfo {
  graphQLErrors?: ReadonlyArray<GraphQLError>;
  networkError?: Error | ServerError | ServerParseError;
  operationName: string;
}

function notifySingleError(error: ErrorInfo): void {
  if (error.networkError) {
    onNetworkError(error.networkError);
  } else {
    // For any given GraphQL query, only show the first message. It's possible the query could have errored
    // in different parts for different reasons, but this avoids overloading the user with too many errors:
    //   - If the errors are for authz reasons, we shouldn't actually be throwing errors, but gracefully show
    //     the user a toast so they know they don't have access. (This doesn't change for 1 vs many errors)
    //   - If our server errored, they can't access the data (which, again, doesn't change for 1 vs many
    //     errors)
    const message = error.graphQLErrors && error.graphQLErrors.length ? error.graphQLErrors[0].message : null;
    if (message != null) {
      notifications.error(message);
    } else {
      notifyGenericGraphQLError();
    }
  }
}

function notifyAllErrors(errors: Array<ErrorInfo>): void {
  const errorsByOpName = _.groupBy(errors, (e) => e.operationName);

  for (const opErrors of Object.values(errorsByOpName)) {
    // opErrors may be as large as BatchHttpLink::batchMax (see apolloClient.tsx). Only show one error per
    // operation name (e.g. "GetBioentity"), since they're likely correlated failures with generic messages.
    notifySingleError(opErrors[0]);
  }
}

export default function createErrorLink(): ApolloLink {
  let pendingErrors: Array<ErrorInfo> = [];

  const debouncedNotifyErrors = debounce(() => {
    notifyAllErrors(pendingErrors);
    pendingErrors = [];
  }, 1 /* ms */);

  return onError(({graphQLErrors, networkError, operation}) => {
    // See https://github.com/apollographql/apollo-link/tree/master/packages/apollo-link-error

    // This function will be called once for each GraphQL query that errors. For example, if 10 chips render,
    // each performing the same query with different variables, and each one errors, this function will be
    // called 10 times, regardless of whether they were batched into the same network request and merged on
    // the backend. (Variables must be different since apollo-link-dedup will deduplicate them otheriwse).

    // We debounce showing toasts so that we can limit how many errors each operation shows per network
    // request (e.g. if 10 GetBioentity queries in the same network request all fail, we only want to error
    // once).

    pendingErrors.push({
      graphQLErrors,
      networkError: networkError ?? undefined,
      operationName: operation.operationName,
    });
    debouncedNotifyErrors();
  });
}
